import React from "react";
import styled, { css } from "styled-components";
import { colors } from "utils";
import { SEPARATOR_GRAY, WHITE, interText } from "components/pages/WarmUpPicker/styles";
import { CollapseIcon, ExpandIcon, LessonCopyIcon, LessonEditIcon, LessonTrashIcon, PieChartIcon } from "./oneListIcons";

// The Lesson card of Manage Groups as one list (Figma 6462:6924 compact,
// 6462:7128 expanded): today's lesson bundle with Figma's header. Its edit
// controls and body are the default card's, handed in ready-made.

const ERROR_ORANGE = "#BA4A21";
const STROKE_INK = colors.nightlyWoods950;
const BODY_BACKGROUND = "#F9FAFD";
const CARD_SHADOW = "0 1px 2px 0 rgba(14, 26, 49, 0.05)";
const COLLAPSE_MS = 250;

// The list names each lesson card's Edit, so it can move focus there.
export const lessonEditButtonId = (assignmentId) => `lesson-card-edit-${assignmentId}`;

const metaItems = ({ lastUpdated, sessionLabel, groupCount }) =>
  [
    lastUpdated && `Last updated: ${lastUpdated}`,
    sessionLabel && `Session Length: ${sessionLabel}`,
    `${groupCount} ${groupCount === 1 ? "Group" : "Groups"}`,
  ].filter(Boolean);

const MetaRow = ({ items }) => (
  <Meta>
    {items.map((item, index) => (
      <MetaItem key={item}>
        <span>{item}</span>
        {index < items.length - 1 && (
          <>
            {" "}
            <Separator aria-hidden="true">|</Separator>
          </>
        )}
      </MetaItem>
    ))}
  </Meta>
);

export const OneListLessonCard = ({
  bundle,
  title,
  eyebrow,
  lastUpdated,
  sessionLabel,
  groupCount,
  isEditing,
  expanded,
  onToggleExpanded,
  isOver,
  dropRef,
  onEnterEdit,
  editButtonId,
  onCopyLesson,
  onArchive,
  editControls,
}) => {
  const titleId = `lesson-card-title-${bundle.assignment_id}`;
  const bodyId = `lesson-card-body-${bundle.assignment_id}`;
  return (
    <Card ref={dropRef} aria-labelledby={titleId} $isOver={isOver}>
      <Header $expanded={expanded} $isOver={isOver}>
        <LeftBlock>
          <Tag>Lesson Groups</Tag>
          <HeaderBlock>
            <PieChartIcon />
            <TextColumn>
              <div>
                <EyebrowRow>
                  {eyebrow ? (
                    <Eyebrow>{eyebrow}</Eyebrow>
                  ) : (
                    <>
                      <EyebrowLead>Lesson for</EyebrowLead>
                      <Eyebrow>Core STANDARD - {bundle.primary_standard_code}</Eyebrow>
                    </>
                  )}
                  {isEditing && editControls.changeLessonFocus}
                </EyebrowRow>
                <Title id={titleId}>
                  {isEditing ? (
                    title
                  ) : (
                    <TitleButton
                      type="button"
                      onClick={onToggleExpanded}
                      aria-expanded={expanded ? "true" : "false"}
                      aria-controls={bodyId}
                    >
                      {title}
                    </TitleButton>
                  )}
                </Title>
              </div>
              {isEditing && editControls.sessionLengthRow ? (
                editControls.sessionLengthRow
              ) : (
                <MetaRow items={metaItems({ lastUpdated, sessionLabel, groupCount })} />
              )}
            </TextColumn>
          </HeaderBlock>
        </LeftBlock>
        <Controls>
          {isEditing && editControls.save ? (
            editControls.save
          ) : (
            <>
              {!isEditing && onEnterEdit && (
                <EditButton type="button" id={editButtonId} onClick={onEnterEdit}>
                  <LessonEditIcon />
                  <EditLabel>Edit</EditLabel>
                </EditButton>
              )}
              {onCopyLesson && (
                <CopyButton type="button" onClick={onCopyLesson} aria-label="Copy lesson" title="Copy Lesson">
                  <LessonCopyIcon />
                </CopyButton>
              )}
              {onArchive && (
                <ArchiveButton type="button" onClick={onArchive} aria-label="Archive lesson" title="Archive Lesson">
                  <LessonTrashIcon />
                </ArchiveButton>
              )}
              <ToggleButton
                type="button"
                onClick={onToggleExpanded}
                aria-expanded={expanded ? "true" : "false"}
                aria-controls={bodyId}
                aria-label={`${expanded ? "Collapse" : "Expand"} ${title}`}
              >
                {expanded ? <CollapseIcon /> : <ExpandIcon />}
              </ToggleButton>
            </>
          )}
        </Controls>
      </Header>
      <Body $expanded={expanded}>
        <BodyClip
          id={bodyId}
          $expanded={expanded}
          inert={expanded ? undefined : ""}
          aria-hidden={expanded ? undefined : "true"}
        >
          {editControls.body}
        </BodyClip>
      </Body>
    </Card>
  );
};

// The card's 1px stroke is drawn over the content, inside the box, so neither
// the stroke nor expanding shifts anything; a drag over it turns it blue.
const Card = styled.article`
  background: ${WHITE};
  border: none;
  border-radius: 12px;
  box-shadow: ${(p) => (p.$isOver ? `0 0 0 2px ${colors.blue300}` : CARD_SHADOW)};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;

  &::after {
    border: 1px solid ${(p) => (p.$isOver ? colors.blue500 : STROKE_INK)};
    border-radius: 12px;
    bottom: 0;
    content: "";
    display: block;
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

// The card's own tab stop: reads exactly as the heading text it replaces.
const TitleButton = styled.button`
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  display: block;
  font: inherit;
  letter-spacing: inherit;
  margin: 0;
  padding: 0;
  text-align: left;
  user-select: none;
  width: 100%;

  &:focus-visible {
    outline: none;
  }

  &::after {
    bottom: 0;
    content: "";
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

// Expanded, the body's 1px separator is the header's last row in Figma, so
// the header gives up one pixel of its bottom padding to keep 136 in all.
// Its states follow the title button: hovered (not while a code is dragged
// over), it fills Blue/50; focused by keyboard, it is ringed 5px outside and
// raised over the card's stroke, which the ring replaces. The card does not
// clip, so the ring is drawn whole; expanded, its bottom edge covers the
// divider.
const Header = styled.div`
  align-items: center;
  background: ${WHITE};
  border-radius: ${(p) => (p.$expanded ? "12px 12px 0 0" : "12px")};
  display: flex;
  isolation: isolate;
  padding: 16px 24px ${(p) => (p.$expanded ? "15px" : "16px")} 0;
  position: relative;

  ${(p) =>
    !p.$isOver &&
    css`
      &:has(${TitleButton}:hover) {
        background: ${colors.blue50};
      }
    `}

  &:has(${TitleButton}:focus-visible) {
    outline: 5px solid ${colors.blue800};
    outline-offset: 0;
    z-index: 1;
  }
`;

const LeftBlock = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

// Flush with the card's left edge, a pill on the right only; Figma's width.
export const tagStyles = css`
  ${interText(14, 20, 700, "0")}
  align-self: flex-start;
  border-radius: 0 200px 200px 0;
  box-sizing: border-box;
  color: ${WHITE};
  padding: 2px 8px;
  white-space: nowrap;
`;

const Tag = styled.span`
  ${tagStyles}
  background: ${colors.nightlyWoods500};
  width: 118px;
`;

const HeaderBlock = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 24px;
`;

const TextColumn = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const EyebrowRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const eyebrowStyles = css`
  color: ${ERROR_ORANGE};
  font-family: "ABC Marfa", sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 18px;
  text-transform: uppercase;
`;

const Eyebrow = styled.span`
  ${eyebrowStyles}
`;

const EyebrowLead = styled.span`
  ${interText(12, 18, 400, "0")}
`;

const Title = styled.h3`
  ${interText(18, 26, 700)}
  margin: 0;
`;

// Figma puts one space before each "|" and 5px after it.
const Meta = styled.div`
  ${interText(14, 20, 400, "0")}
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const MetaItem = styled.span`
  white-space: nowrap;
`;

const Separator = styled.span`
  color: ${SEPARATOR_GRAY};
`;

const Controls = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 24px;
`;

// Raised over the title button's stretched hit area, so each control keeps its
// own clicks while the gaps between them still toggle the card.
const plainButton = css`
  align-items: center;
  background: transparent;
  border: none;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  padding: 0;
  position: relative;
  z-index: 1;
`;

// Figma "Button/Text Only": pencil, then the label in one 4px frame; Figma's width.
const EditButton = styled.button`
  ${plainButton}
  border-radius: 6px;
  height: 48px;
  padding: 0 4px;
  width: 74px;

  &:hover span {
    text-decoration: underline;
  }
`;

const EditLabel = styled.span`
  ${interText(18, 26)}
  color: ${colors.blue700};
  padding: 0 4px;
`;

const CopyButton = styled.button`
  ${plainButton}
  border-radius: 6px;
  height: 24px;
  width: 24px;

  &:hover {
    background: ${colors.blue50};
  }
`;

const ArchiveButton = styled.button`
  ${plainButton}
  border-radius: 6px;
  height: 40px;
  width: 40px;

  &:hover {
    background: ${colors.blue50};
  }
`;

const ToggleButton = styled.button`
  ${plainButton}
  background: ${colors.blue500};
  border-radius: 6px;
  height: 40px;
  padding: 8px;
  width: 40px;
`;

const Body = styled.div`
  background: ${BODY_BACKGROUND};
  border-radius: 0 0 12px 12px;
  display: grid;
  grid-template-rows: ${(p) => (p.$expanded ? "1fr" : "0fr")};
  overflow: hidden;
  transition: grid-template-rows ${COLLAPSE_MS}ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Collapsed, the body leaves the tab order and the accessibility tree once it
// has finished closing (visibility waits for the height transition).
const BodyClip = styled.div`
  min-height: 0;
  overflow: hidden;
  transition: visibility 0s linear ${(p) => (p.$expanded ? "0s" : `${COLLAPSE_MS}ms`)};
  visibility: ${(p) => (p.$expanded ? "visible" : "hidden")};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

